import { Store, useAppDispatch } from 'src/redux/Store';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import { COLORS } from 'src/constants/Color';

import { CSSProperties } from '@material-ui/core/styles/withStyles';
import {
  getPaymentFilter,
  getPaymentRangeDate,
  normalizePaymentForUser,
} from 'src/lib/PaymentHelper';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { SEARCH_LOGICAL_OPERATORS } from 'src/models/AdvanceSearchModel';
import { SEARCH_OPERATORS_PRIMITIVE } from 'src/constants/SearchOperators';
import {
  getEntitiesAction,
  resetQueryBuilderResult,
  setFilters,
} from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { PaymentSections } from 'src/components/Pages/UserPaymentsPage';
import PaymentModel, { PAYMENT_STATUS, PAYMENT_TYPE } from 'src/models/PaymentModel';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  getUserBankAccountAction,
  getUserCreditCardAction,
} from 'src/redux/actions/paymentInfoAction';
import { CreditCardModel } from 'src/models/CreditCardModel';
import { BankAccountModel } from 'src/models/BankAccountModel';
import { resetSelections, setSelections } from 'src/redux/actions/selectionActions';
import ViewUserPaymentButton from 'src/components/Control/Buttons/ViewUserPaymentButton';
import RetryUserPaymentButton from 'src/components/Control/Buttons/RetryUserPaymentButton';
import Row from 'src/components/UI/Row';
import UserCardItem from 'src/components/Elements/User/UserCardItem';
import palette from 'src/styles/palette';
import UserListItemLabel from 'src/components/Elements/User/UserCardLabel';
import { normalizeForForm } from 'src/lib/QueryBuilderHelper';
import Col2 from 'src/components/UI/Col2';
import Col4 from 'src/components/UI/Col4';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';

interface Props {
  section: PaymentSections,
  order: string,
}

const ManageUserPayments = (props: Props) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 680px)' });
  const dispatch = useAppDispatch();

  const { section, order } = props;

  const MAX_ITEMS = 7;

  const selections = useSelector((state: Store) => {
    if (!!state.selected[ENTITIES.payments]) {
      return [...state.selected[ENTITIES.payments]];
    }
    return [];
  });

  const loading = useSelector(
    (state: Store) => state.formStatus[FORM.query_builder] === FORM_STATUS.loading
      || state.formStatus[FORM.get_user_credit_cards] === FORM_STATUS.loading
      || state.formStatus[FORM.get_user_bank_accounts] === FORM_STATUS.loading,
  );

  const payments = useSelector(
    (state: Store) => state.querybuilder.payments?.result?.list || [],
  );

  const sortedPayments = payments.map(
    (invoice) => normalizeForForm(invoice, ENTITIES.payments),
  ).sort((a:any, b:any) => {
    const dateA = new Date(a.paid_date);
    const dateB = new Date(b.paid_date);
    if (order === 'asc') {
      return dateA.getTime() - dateB.getTime();
    }
    return dateB.getTime() - dateA.getTime();
  });

  const contactId = useSelector((state: Store) => state.user.organisation_contact_id);

  const organisation = useSelector((state: Store) => state.currentOrganisation.id);

  const creditCards = useSelector(
    (state: Store) => {
      if (!!state.userCreditCards) {
        return state.userCreditCards as CreditCardModel[];
      }
      return [] as CreditCardModel[];
    },
  );

  const bankAccounts = useSelector(
    (state: Store) => {
      if (!!state.userBankAccounts) {
        return state.userBankAccounts as BankAccountModel[];
      }
      return [] as BankAccountModel[];
    },
  );

  const getCreditCard = (id: number) => {
    if (!id) return 'N/A';
    const result = creditCards.find((creditCard) => creditCard.id === id);
    if (!result) return 'N/A';
    return result.masked_card_number;
  };

  const getBankAccount = (id: number) => {
    if (!id) return 'N/A';
    const result = bankAccounts.find((bankAccount) => bankAccount.id === id);
    if (!result) return 'N/A';
    return result.account.slice(-4);
  };

  const getFiltered = (): PaymentModel[] => sortedPayments.map((item: any) => {
    const payment = normalizePaymentForUser(item);

    if (section === PaymentSections.all) {
      return payment;
    }
    if (section === PaymentSections.scheduled) {
      if (![
        PAYMENT_STATUS.scheduled,
        PAYMENT_STATUS.pending,
        PAYMENT_STATUS.holding,
      ].includes(payment.payment_status as PAYMENT_STATUS)) {
        return undefined;
      }
    }
    if (section === PaymentSections.error) {
      if (![
        PAYMENT_STATUS.error,
        PAYMENT_STATUS.declined,
      ].includes(payment.payment_status as PAYMENT_STATUS)) {
        return undefined;
      }
    }
    if (section === PaymentSections.paid) {
      if (payment.payment_status !== PAYMENT_STATUS.paid) {
        return undefined;
      }
    }
    return payment;
  }).filter((item: any): item is PaymentModel => item !== undefined);

  const [page, setPage] = useState(1);
  const [items, setItems] = useState(
    getFiltered().slice(0, MAX_ITEMS),
  );
  const [hasMore, setHasMore] = useState(true);
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const scrollableDivRef = useRef<HTMLDivElement>(null);

  const resetScroll = () => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0; // Reset vertical scroll
    }
  };

  const loadPayments = () => {
    const [startDate, endDate] = getPaymentRangeDate(section);
    const filters = getPaymentFilter(
      startDate,
      endDate,
    );
    if (contactId) {
      filters.push(['payments.contact_id', SEARCH_OPERATORS_PRIMITIVE.eq, contactId]);
    }
    dispatch(setFilters({
      entity: ENTITIES.payments,
      filters: { [SEARCH_LOGICAL_OPERATORS.and]: filters },
    }));
    dispatch(getEntitiesAction(ENTITIES.payments));
  };

  useEffect(() => {
    const filteredItems = getFiltered();
    setPage(1);
    setHasMore(filteredItems.length > MAX_ITEMS);
    setItems(filteredItems.slice(0, MAX_ITEMS));
    resetScroll();
  }, [section, order, payments]);

  useEffect(() => {
    dispatch(resetQueryBuilderResult({ entity: ENTITIES.payments }));
    dispatch(resetSelections({ entity: ENTITIES.payments }));
  }, [organisation]);

  useEffect(() => {
    loadPayments();
    dispatch(getUserCreditCardAction());
    dispatch(getUserBankAccountAction());
  }, [organisation, contactId]);

  const onClickSelect = (payment?: PaymentModel) => {
    if (!payment) return;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { id, payment_status } = payment;
    if (!id || payment_status !== PAYMENT_STATUS.paid) return;
    const index = selections.indexOf(id);

    if (index >= 0) {
      selections.splice(index, 1);
    } else {
      selections.push(id);
    }

    dispatch(setSelections({
      entity: ENTITIES.payments,
      selections,
    }));
  };

  const handleNext = () => {
    const filteredItems = getFiltered();
    const newItems = filteredItems.slice(page * MAX_ITEMS, (page + 1) * MAX_ITEMS);
    setItems((prevItems) => [...prevItems, ...newItems]);
    setPage((prevPage) => prevPage + 1);
    setHasMore(filteredItems.length > (page + 1) * MAX_ITEMS);
  };

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    // Check if the user has scrolled to the bottom (use tolerance for sub-pixel rendering)
    if (scrollHeight - scrollTop <= clientHeight + 1) {
      if (hasMore) {
        handleNext();
      }
    }
  }, [page, hasMore]);

  const renderPaymentInfo = (payment: PaymentModel) => {
    if (payment.payment_type === PAYMENT_TYPE.cc) {
      return (
        <div>
          <UserListItemLabel>Credit Card Used:</UserListItemLabel>
          {getCreditCard(payment.payment_info_id as number)}
        </div>
      );
    }
    if (payment.payment_type === PAYMENT_TYPE.eft) {
      return (
        <div>
          <UserListItemLabel>Credit Card Used:</UserListItemLabel>
          ********{getBankAccount(payment.payment_info_id as number)}
        </div>
      );
    }
    return (<></>);
  };

  const renderScheduledDate = (payment: PaymentModel) => {
    if (payment.payment_status !== PAYMENT_STATUS.scheduled) return (<></>);
    if (!payment.scheduled_date) return (<></>);
    const date = moment(payment.scheduled_date)
      .format('YYYY-MM-DD');
    return (<><b>Scheduled</b>{date}</>);
  };

  const renderPaidDate = (payment: PaymentModel) => {
    if (!payment.paid_date) return (<></>);
    const date = moment(payment.paid_date)
      .format('YYYY-MM-DD');
    return (<><b>Date</b>{date}</>);
  };

  const getItemStyle = (id?: number) => {
    if (selections.indexOf(id) < 0) {
      return {
        backgroundColor: '#FFF',
      };
    }
    return {
      border: `1px solid ${palette.primary.main}`,
      backgroundColor: lighten(palette.primary.main, 0.9),
    };
  };

  const getRowStyle = () => {
    if (!isTabletOrMobile) {
      return {
        minWidth: '640px',
      } as CSSProperties;
    }
    return {} as CSSProperties;
  };

  const renderItem = (payment: PaymentModel) => (
    <Row style={getRowStyle()} key={payment.id}>
      <UserCardItem
        style={getItemStyle(payment.id)}
        onClick={() => onClickSelect(payment)}
        itemNumber={payment.id}>
        <Row style={{ justifyContent: 'space-between' }}>
          <span style={{ color: COLORS.secondary, fontSize: '16px' }}>#{payment.id} - {payment.title}</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <RetryUserPaymentButton payment={payment} />
            <ViewUserPaymentButton payment={payment} />
          </div>
        </Row>
        { !isTabletOrMobile && (
          <>
            <Row>
              <Col4>
                {renderPaidDate(payment)}
              </Col4>
              <Col4>
                {renderScheduledDate(payment)}
              </Col4>
              <Col4>
                <b>Amount</b>
                {formatter.format(Number(payment.amount ?? 0))}
              </Col4>
              <Col4>
                <b>Deductible</b>
                {formatter.format(payment.deductible ?? 0)}
              </Col4>
            </Row>
            <Row>
              <Col4>
                <b>Method</b>{payment.payment_type}
              </Col4>
              <Col4>
                <b>Status</b>{payment.payment_status}
              </Col4>
            </Row>
          </>
        )}
        { isTabletOrMobile && (
          <>
            <Row>
              <Col2>
                {renderPaidDate(payment)}
              </Col2>
              <Col2>
                {renderScheduledDate(payment)}
              </Col2>
            </Row>
            <Row>
              <Col2>
                <b>Amount</b>
                {formatter.format(Number(payment.amount ?? 0))}
              </Col2>
              <Col2>
                <b>Deductible</b>
                {formatter.format(payment.deductible ?? 0)}
              </Col2>
            </Row>
            <Row>
              <Col2>
                <b>Method</b>{payment.payment_type}
              </Col2>
              <Col2>
                <b>Status</b>{payment.payment_status}
              </Col2>
            </Row>
          </>
        )}
        {renderPaymentInfo(payment)}
      </UserCardItem>
    </Row>
  );

  const getListStyle = () => {
    if (isTabletOrMobile) {
      return {
        marginTop: '48px',
        height: '585px',
        overflowY: 'auto',
      } as CSSProperties;
    }
    return {
      marginTop: '0px',
      overflowY: 'auto',
      maxWidth: '680px',
      margin: 'auto',
      height: '585px',
    } as CSSProperties;
  };

  const renderItems = () => {
    if (loading) {
      return (
        <div style={{ width: '40px', margin: '0 auto', marginTop: '32px' }}>
          <CircularProgress size={40} />
        </div>
      );
    }
    return items.map((item) => renderItem(item as PaymentModel));
  };

  return (
    <div style={getListStyle()} onScroll={handleScroll} ref={scrollableDivRef}>
      {renderItems()}
    </div>
  );
};

export default ManageUserPayments;
